// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Users, UserPlus, AlertCircle, Search, CheckCircle2, XCircle } from 'lucide-react'
import { listMembers, ApiError, type OrgMember } from '@/lib/api'
import { useAuth } from '@/context/auth-context'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PersonAvatar } from '@/components/ui/person-avatar'
import InviteMemberModal from '@/components/InviteMemberModal'
import MemberActions, { type ActionResult } from '@/components/MemberActions'
import AccessDenied from '@/components/AccessDenied'

type PageState = 'loading' | 'error' | 'forbidden' | 'empty' | 'list'

// Map a member status → badge variant (invited reuses the amber "suspended" look).
function statusVariant(status: string) {
  switch (status) {
    case 'active': return 'active' as const
    case 'invited': return 'suspended' as const
    default: return 'default' as const
  }
}

// Humanize an unknown snake_case value as a last-resort label.
function humanize(s: string): string {
  if (!s) return ''
  const spaced = s.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

function SkeletonRow() {
  return (
    <tr className="border-b border-border last:border-0">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full shimmer shrink-0" />
          <div className="flex flex-col gap-1.5">
            <div className="h-3.5 w-48 rounded shimmer" />
            <div className="h-3 w-24 rounded shimmer" />
          </div>
        </div>
      </td>
      <td className="px-4 py-3"><div className="h-5 w-20 rounded-full shimmer" /></td>
      <td className="px-4 py-3"><div className="h-5 w-16 rounded-full shimmer" /></td>
      <td className="px-4 py-3" />
    </tr>
  )
}

export default function TeamPage() {
  const { t } = useTranslation()
  const { role } = useAuth()

  const [members, setMembers] = useState<OrgMember[]>([])
  const [pageState, setPageState] = useState<PageState>('loading')
  const [errorMsg, setErrorMsg] = useState('')
  const [search, setSearch] = useState('')
  const [inviteOpen, setInviteOpen] = useState(false)
  const [actionResult, setActionResult] = useState<ActionResult | null>(null)

  const roleLabel = useCallback(
    (r: string) => (r ? t(`team.role_${r}`, { defaultValue: humanize(r) }) : ''),
    [t],
  )

  const fetchMembers = useCallback(async () => {
    setPageState('loading')
    setErrorMsg('')
    try {
      const data = await listMembers()
      setMembers(data)
      setPageState(data.length === 0 ? 'empty' : 'list')
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setPageState('forbidden')
      } else {
        setErrorMsg(err instanceof Error ? err.message : String(err))
        setPageState('error')
      }
    }
  }, [])

  useEffect(() => { void fetchMembers() }, [fetchMembers])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return members
    return members.filter(
      (m) =>
        m.email.toLowerCase().includes(q) ||
        (m.fullName ?? '').toLowerCase().includes(q) ||
        roleLabel(m.role).toLowerCase().includes(q),
    )
  }, [members, search, roleLabel])

  if (pageState === 'forbidden') {
    return <AccessDenied />
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#012031] via-[#033C85] to-[#006EF9]">
        <div className="absolute -bottom-16 -right-16 w-[220px] h-[220px] rounded-full bg-[#02C7C3]/20 blur-3xl pointer-events-none" />
        <div className="relative z-10 px-6 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-white/15 border border-white/25 flex items-center justify-center shrink-0">
              <Users className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white leading-tight">{t('team.title')}</h1>
              <p className="text-white/65 text-sm mt-1">{t('team.subtitle')}</p>
            </div>
          </div>
          <Button
            onClick={() => setInviteOpen(true)}
            className="w-full sm:w-auto bg-white text-[#033C85] hover:bg-white/90 border-0 font-semibold shadow-lg"
          >
            <UserPlus className="h-4 w-4" />
            {t('team.invite')}
          </Button>
        </div>
      </div>

      {/* Transient result of a member action (resend / role / remove). */}
      {actionResult && (
        <div className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl border text-sm ${actionResult.kind === 'success' ? 'bg-accent/10 border-accent/25 text-foreground' : 'bg-destructive/8 border-destructive/20 text-destructive'}`}>
          {actionResult.kind === 'success' ? <CheckCircle2 className="h-4 w-4 shrink-0 text-accent" /> : <XCircle className="h-4 w-4 shrink-0" />}
          <span>{actionResult.text}</span>
          <button onClick={() => setActionResult(null)} className="ml-auto text-xs text-muted-foreground hover:text-foreground">✕</button>
        </div>
      )}

      {/* Loading skeleton */}
      {pageState === 'loading' && (
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <table className="w-full" role="table">
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)}
            </tbody>
          </table>
        </div>
      )}

      {/* Error state */}
      {pageState === 'error' && (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <div className="h-14 w-14 rounded-2xl bg-destructive/10 flex items-center justify-center">
            <AlertCircle className="h-7 w-7 text-destructive" />
          </div>
          <p className="text-sm font-medium text-foreground">{t('team.loadError')}</p>
          {errorMsg && <p className="text-xs text-muted-foreground max-w-xs">{errorMsg}</p>}
          <Button variant="outline" size="sm" onClick={() => void fetchMembers()} className="mt-1">
            {t('team.retry')}
          </Button>
        </div>
      )}

      {/* Empty state */}
      {pageState === 'empty' && (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <div className="h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center">
            <Users className="h-7 w-7 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-foreground">{t('team.empty')}</p>
          <p className="text-xs text-muted-foreground max-w-xs">{t('team.emptyBody')}</p>
          <Button size="sm" onClick={() => setInviteOpen(true)} className="mt-1">
            {t('team.invite')}
          </Button>
        </div>
      )}

      {/* List */}
      {pageState === 'list' && (
        <div className="flex flex-col gap-4">
          {/* Toolbar: search + count */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                type="search"
                className="pl-10"
                placeholder={t('team.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              {t('team.memberCount', { count: members.length })}
            </span>
          </div>

          {/* Table */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <table className="w-full" role="table">
              <thead>
                <tr className="border-b border-border bg-secondary/50">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {t('team.colMember')}
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {t('team.colRole')}
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {t('team.colStatus')}
                  </th>
                  <th className="px-4 py-2.5 w-12" />
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-12 text-center text-sm text-muted-foreground">
                      {t('team.noResults')}
                    </td>
                  </tr>
                ) : (
                  filtered.map((m) => (
                    <tr key={m.id} className="border-b border-border last:border-0 hover:bg-secondary/40 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <PersonAvatar seed={m.uid || m.email} size={36} />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{m.email}</p>
                            <p className="text-xs text-muted-foreground truncate">{m.fullName || '—'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge>{roleLabel(m.role)}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={statusVariant(m.status)}>
                          {t(`team.status_${m.status}`, { defaultValue: humanize(m.status) })}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <MemberActions
                          member={{ id: m.id, role: m.role, status: m.status }}
                          callerRole={role}
                          onChanged={() => void fetchMembers()}
                          onResult={setActionResult}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <InviteMemberModal
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        callerRole={role}
        onCreated={() => void fetchMembers()}
      />
    </div>
  )
}
